import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { theme } from 'styles/theme'

// Faster than the browser's title tooltip
const SHOW_DELAY_MS = 150
// Leaves time to move the mouse from the element onto the tooltip
const HIDE_DELAY_MS = 200
// Above the dialog's screen glass
const TOOLTIP_Z_INDEX = 400
// Space kept between the tooltip and its element, and between the tooltip and the window's edges
const GAP = 4
const WINDOW_MARGIN = 8

// While its tooltip is shown, an element's title is kept here, so the browser doesn't show its own tooltip too
const TAKEN_TITLE_ATTRIBUTE = 'data-tooltip-title'
// Marks an aria-label that was set from the title, for an element without another accessible name
const TITLE_ARIA_LABEL_ATTRIBUTE = 'data-tooltip-aria-label'
const TITLED_ELEMENT_SELECTOR = `[title]:not([title=""]), [${TAKEN_TITLE_ATTRIBUTE}]`
// Only the titles inside the containers marked with withCustomTooltips get our tooltip
const CUSTOM_TOOLTIPS_ATTRIBUTE = 'data-custom-tooltips'
export const withCustomTooltips = { [CUSTOM_TOOLTIPS_ATTRIBUTE]: '' } as const
// Google Maps manages the titles of its own elements
const EXCLUDED_CONTAINERS_SELECTOR = '.gm-style'

const findTitledElement = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return null
  }
  const element = target.closest(TITLED_ELEMENT_SELECTOR)
  return element instanceof HTMLElement
    && element.closest(`[${CUSTOM_TOOLTIPS_ATTRIBUTE}]`) !== null
    && element.closest(EXCLUDED_CONTAINERS_SELECTOR) === null
    ? element
    : null
}

// Moves the title aside, and returns it
const takeTitle = (element: HTMLElement) => {
  const title = element.getAttribute('title')
  if (title !== null) {
    element.setAttribute(TAKEN_TITLE_ATTRIBUTE, title)
    element.removeAttribute('title')
  }
  const takenTitle = element.getAttribute(TAKEN_TITLE_ATTRIBUTE) ?? ''
  // Without the title, an element with no text and no label would have no name for screen readers
  const hasAccessibleName = element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby') || element.textContent?.trim()
  if (!hasAccessibleName) {
    element.setAttribute('aria-label', takenTitle)
    element.setAttribute(TITLE_ARIA_LABEL_ATTRIBUTE, '')
  }
  return takenTitle
}

const restoreTitle = (element: HTMLElement) => {
  const takenTitle = element.getAttribute(TAKEN_TITLE_ATTRIBUTE)
  element.removeAttribute(TAKEN_TITLE_ATTRIBUTE)
  // React may have set a new title meanwhile
  if (takenTitle !== null && !element.hasAttribute('title')) {
    element.setAttribute('title', takenTitle)
  }
  if (element.hasAttribute(TITLE_ARIA_LABEL_ATTRIBUTE)) {
    element.removeAttribute('aria-label')
    element.removeAttribute(TITLE_ARIA_LABEL_ATTRIBUTE)
  }
}

const TooltipContainer = styled.div`
  position: fixed;
  z-index: ${TOOLTIP_Z_INDEX};
  max-inline-size: 20rem;
  padding: 0.4em 0.6em;
  border-radius: ${theme.radii.tooltipRadii};
  background-color: ${theme.colors.MainBlack};
  color: ${theme.colors.White};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.regular};
  line-height: ${theme.lineHeights.normal};
  text-align: start;
  text-transform: none;
  letter-spacing: normal;
  white-space: pre-line;
  overflow-wrap: anywhere;
  user-select: text;
  cursor: text;
`

interface TooltipPopupProps {
  anchor: HTMLElement
  text: string
}

// Above its element, or below it when there's no room above, and inside the window
const TooltipPopup = ({ anchor, text, tooltipRef }: TooltipPopupProps & { tooltipRef: React.RefObject<HTMLDivElement> }) => {
  const [position, setPosition] = useState<{ top: number, left: number } | null>(null)

  useLayoutEffect(() => {
    const anchorRect = anchor.getBoundingClientRect()
    const tooltipRect = tooltipRef.current?.getBoundingClientRect()
    if (tooltipRect === undefined) {
      return
    }
    const top = anchorRect.top - tooltipRect.height - GAP >= WINDOW_MARGIN
      ? anchorRect.top - tooltipRect.height - GAP
      : anchorRect.bottom + GAP
    const maxLeft = window.innerWidth - tooltipRect.width - WINDOW_MARGIN
    setPosition({ top, left: Math.max(WINDOW_MARGIN, Math.min(anchorRect.left, maxLeft)) })
  }, [anchor, text, tooltipRef])

  return createPortal(
    <TooltipContainer {...{
      ref: tooltipRef,
      role: 'tooltip',
      // Hidden until it is measured and positioned
      style: position ?? { top: 0, left: 0, visibility: 'hidden' },
    }}>{ text }</TooltipContainer>,
    document.body,
  )
}

// Shows the titles of the elements inside the containers marked with withCustomTooltips in our own tooltips, instead
// of the browser's: they show sooner, and their text can be selected and copied. Rendered once for the whole app.
export const TooltipManager = () => {
  const [shownTooltip, setShownTooltip] = useState<TooltipPopupProps | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // The element whose title was taken, while its tooltip is about to show or is shown
    let anchor: HTMLElement | null = null
    let isShown = false
    let timeout: number | undefined
    let titleObserver: MutationObserver | null = null

    const schedule = (callback: VoidFunction, delayMs: number) => {
      window.clearTimeout(timeout)
      timeout = window.setTimeout(callback, delayMs)
    }
    const cancelScheduled = () => { window.clearTimeout(timeout) }

    const release = () => {
      cancelScheduled()
      titleObserver?.disconnect()
      titleObserver = null
      if (anchor !== null) {
        restoreTitle(anchor)
      }
      anchor = null
      isShown = false
      setShownTooltip(null)
    }

    const take = (element: HTMLElement) => {
      release()
      anchor = element
      let text = takeTitle(element)
      // React sets the title again when it changes, so take the new one
      titleObserver = new MutationObserver(() => {
        if (element.hasAttribute('title')) {
          text = takeTitle(element)
          if (isShown) {
            setShownTooltip({ anchor: element, text })
          }
        }
      })
      titleObserver.observe(element, { attributes: true, attributeFilter: ['title'] })
      schedule(() => {
        isShown = true
        setShownTooltip({ anchor: element, text })
      }, SHOW_DELAY_MS)
    }

    const isInTooltip = (node: unknown) => node instanceof Node && (tooltipRef.current?.contains(node) ?? false)
    const isInAnchorOrTooltip = (node: unknown) => isInTooltip(node) || (node instanceof Node && (anchor?.contains(node) ?? false))
    const isTextSelectedInTooltip = () => {
      const selection = window.getSelection()
      return selection !== null && !selection.isCollapsed && isInTooltip(selection.anchorNode)
    }

    const onMouseOver = (e: MouseEvent) => {
      if (isInTooltip(e.target)) {
        cancelScheduled()
        return
      }
      const element = findTitledElement(e.target)
      if (element === null) {
        return
      }
      if (element === anchor) {
        // Back on the element before its tooltip was hidden
        if (isShown) {
          cancelScheduled()
        }
        return
      }
      take(element)
    }

    const onMouseOut = (e: MouseEvent) => {
      if (anchor === null || !isInAnchorOrTooltip(e.target) || isInAnchorOrTooltip(e.relatedTarget)) {
        return
      }
      if (!isShown) {
        release()
      } else if (isTextSelectedInTooltip()) {
        // Stays open while its text is selected, until a click elsewhere
        cancelScheduled()
      } else {
        schedule(release, HIDE_DELAY_MS)
      }
    }

    // Like the browser's tooltip, a click hides it
    const onMouseDown = (e: MouseEvent) => {
      if (!isInTooltip(e.target)) {
        release()
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        release()
      }
    }
    // The tooltip would stay where its element was
    const onScroll = (e: Event) => {
      if (!isInTooltip(e.target)) {
        release()
      }
    }

    document.addEventListener('mouseover', onMouseOver)
    document.addEventListener('mouseout', onMouseOut)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true) // Capture, to also hide on scrolling of the element's containers
    return () => {
      release()
      document.removeEventListener('mouseover', onMouseOver)
      document.removeEventListener('mouseout', onMouseOut)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [])

  return shownTooltip && <TooltipPopup {...{ ...shownTooltip, tooltipRef }} />
}
